import { ProviderPrescriberType, ProviderSignOnBonusTier, ProviderTerminationReason, ProviderTerminationType, SeeClientsEnum } from '@uplift/types';
import { Address } from './common';
import { ProviderMatchingProfileBookmark } from '../actions';
import { InsuranceCompany } from './insurance';
import { BusinessEntity } from './businessEntity';
import { ServiceType } from './appointment';


export type Practice = {
  id: string;
  practiceName: string;
  practiceServiceType: SeeClientsEnum;
  role?: string; // review unused?
  userId: string;
};

export enum ClaimBehaviour {
  hold = 'hold',
  submit = 'submit'
}

export enum ProviderInsuranceRosterStatusEnum {
  pendingSubmission = 'pending_submission',
  queued = 'queued',
  sent = 'sent',
  rostering = 'rostering',
  onHold = 'on_hold',
  verified = 'verified',
  ineligible = 'ineligible',
}

export enum ZocDocStatus {
  notStarted = 'not_started',
  activated = 'activated',
  deactivated = 'deactivated',
  optedOut = 'opted_out',
}

export type PracticeLocation = {
  id: string;
  practiceId: string;
  address: Omit<Address, 'line1' | 'line2'> & { line1?: string; line2?: string };
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  inPersonVisits: boolean;
  phone?: string;
  phoneExt?: string;
  streetAddressHidden: boolean;
  practiceServiceType?: SeeClientsEnum | null;
  isHandicapAccessible?: boolean | null;
};

export type ProviderDetail = {
  userId: string;
  fullTime: boolean;
  availableToMatch: boolean;
  prescriberType: ProviderPrescriberType | null;
  collaborativePhysician: boolean;
  caqhId: string | null;
  caqhAttested: boolean | null;
  npi: string | null;
  displayCredentials: string[];
  matchingProfileBookmark: ProviderMatchingProfileBookmark | null;
  createdAt: string;
  updatedAt: string;
  nuccTaxonomyCode?: string;
  verifiableId?: string;
  checkrCandidateId?: string;
  credentialedDates: string[];
  signOnBonusTier: ProviderSignOnBonusTier | null;
  salesforceId: string | null;
  onboardingTermsAcceptedAt: string | null;
  zocdocStatus: ZocDocStatus | null;
  zocdocStatusUpdatedAt: string | null;
};

export type UpliftLicenseType =
  | 'psych_nurse_practitioner'
  | 'psychiatrist'
  | 'psychologist_resident'
  | 'psychologist'
  | 'therapist_resident'
  | 'therapist';

export type ProviderService = {
  stateId: string;
  licenseType: UpliftLicenseType;
  cptCodeId: string;
  serviceType: ServiceType | null;
  insuranceCompanyId: string | null;
};

export type ServiceRate = {
  id: string;
  service: ProviderService;
  priceCents: number;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
};

export type MentalHealthRole = {
  id: string;
  name: string;
  upliftLicenseType: UpliftLicenseType;
  enabled: boolean;
  prefix?: string;
  suffix?: string;
};

export type License = {
  id: string;
  userId: string;
  stateId: string;
  active: boolean;
  live: boolean;
  expiration: string;
  number: string;
  role: MentalHealthRole;
  pendingLive: boolean;
};

export type LicenseCollabAgreement = {
  id: string;
  licenseId: string;
  fileUploadId: string;
  expirationDate: string; // "YYYY-MM-DD"
  physicianFirstName: string;
  physicianLastName: string;
  physicianNpi: string;
  physicianEmail: string;
  physicianPhone: string | null;
  uploadedBy: string;
  uploadedAt: string; // ISO datetime
  archivedBy: string | null;
  archivedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
};

export type ProviderInsurance = {
  id: string;
  userId: string;
  stateId: string;
  insuranceCompany: InsuranceCompany;
  active: boolean;
  expiresAt: string;
  effectiveFrom: string | null;
  rosterSentDate: string | null;
  verifiedDate: string | null;
  createdAt: string | null;
  onHoldDate: string | null;
  rosterStatus: ProviderInsuranceRosterStatusEnum;
  storedRosterStatus: ProviderInsuranceRosterStatusEnum;
  claimBehaviour: ClaimBehaviour;
  businessEntity: BusinessEntity;
  updatedAt: string;
};

export type ProviderInsurancesGroup = {
  title: string;
  isGroup: boolean;
  companies: InsuranceCompany[];
  allInsurances: ProviderInsurance[];
  insurances: ProviderInsurance[];
};

export type Credential = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export type ProviderOverview = {
  userId: string;
  displayName: string;
};

export type PublicProviderProfile = {
  userId: string;
  url: string;
  id: string;
  stateShortName: string;
  syncStatus: 'pending' | 'published' | 'draft' | 'archived' | 'error';
  lastSync: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProviderTerminationData = {
  type: ProviderTerminationType;
  reason: ProviderTerminationReason | null;
  description: string | null;
};
